import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { EditAuthorizationSystemComponent } from '../edit-authorization-system/edit-authorization-system.component';
import { IntegrationFieldValueChanged } from '../../store/actions/integrations.actions';
import { UtilsService } from '../../services/utils/utils.service';

import { IntegrationConfigField, IntegrationsSubSection } from '../../interface/integrationmodal.interface';
import { IntegrationRecord } from '../../models/integrations/integrationrecord.class';
import { MasterField } from '../../models/masters/masterfield.class';
import { MasterTables } from '../../models/masters/mastertables.class';
import { IntegrationsUtilsService } from '../../services/integrations-utils/integrations-utils.service';

@Component({
    selector: 'app-integrations-field-template',
    templateUrl: './integrations-field-template.component.html',
    styleUrls: ['./integrations-field-template.component.scss'],
    standalone: false,
})
export class IntegrationsFieldTemplateComponent implements OnInit {
    @Input() record: IntegrationRecord;
    @Input() field: IntegrationConfigField;
    @Input() masterTableColumns: MasterField[];
    @Input() hide: boolean;
    @Input() configuredRecords: any[];
    @Input() parentRecord: IntegrationRecord;
    @Input() chain = [];
    @Input() sources = [];
    @Input() subSectionRecord?: IntegrationsSubSection['records'][0];
    @Input() masterTables: MasterTables[];
    @Input() subTenants: any[];
    @Input() inConnectorsUI = false;
    @Input() appearance: 'outline' | 'standard' = 'standard';
    @Input() checkAndAddNewRow: () => void;
    @Input() readonly = false;

    @Output() getMasterColumns = new EventEmitter();
    @Output() apiConfigChange = new EventEmitter();
    @Output() configNameChanged = new EventEmitter();
    @Output() chainChanged: EventEmitter<IntegrationConfigField['value']> = new EventEmitter();
    @Output() appChanged: EventEmitter<IntegrationConfigField['value']> = new EventEmitter();
    @Output() transformationsChanged: EventEmitter<IntegrationConfigField['value']> = new EventEmitter();
    @Output() checkValidateButton = new EventEmitter();
    @Output() checkDisableUpdateAndRunNow = new EventEmitter();

    getFieldType = this._integrationUtils.getFieldType;
    toggleFieldValues = this._integrationUtils.toggleFieldValues;
    getOptions = this._integrationUtils.getOptions;
    deleteFieldValue = this._integrationUtils.deleteFieldValue;
    public referenceField: IntegrationConfigField;
    public fileNames: string[] = [];

    constructor(
        public _integrationUtils: IntegrationsUtilsService,
        private _utils: UtilsService,
        private dialog: MatDialog,
        private store$: Store
    ) {}

    isFieldDisabled = () => {
        let disabled = false;
        if (this.field.conditionalDisable) {
            disabled = this._integrationUtils.disableField(
                this.field,
                this.parentRecord ? this.parentRecord : (this.record as any),
                this.parentRecord ? (this.record as any) : undefined
            );
        }
        return disabled;
    };

    toggleValueChanged(field: IntegrationConfigField, _record: IntegrationRecord, value: any): void {
        if (field.id === 'internal') {
            this.checkDisableUpdateAndRunNow.emit(value);
        }
    }

    setPanelTransformation = (field, mapping, value) => {
        if (field.value === undefined) {
            const obj = {};
            obj[mapping.name] = value;
            field.value = obj;
        } else {
            field.value[mapping.name] = value;
        }
    };

    isConditionalArray = (field: IntegrationConfigField) => this.record.isConditionalArray(field);

    valueChanged = $event => {
        this.field.value = $event;
        this.field.errors = [];
        this.isFormValid();
    };

    changeValidateButton = () => {
        if (this.record?.recordData?.api === 'RESTAPI' || this.parentRecord?.recordData?.api === 'RESTAPI') {
            const currentRecord = this.parentRecord || this.record;
            const authorizationSystem =
                currentRecord.getRecordFieldValueByProperty('id', 'authorizationSystem') ||
                currentRecord.getRecordFieldFromSubSections(currentRecord.recordData.subSections, 'id', 'authorizationSystem')?.value;
            // const authType = (currentRecord.getRecordFieldValueByProperty('id', 'type') || currentRecord.getRecordFieldFromSubSections(currentRecord.recordData.subSections, 'id', 'type')?.value);

            if (this.field.id === 'authorizationSystem') {
                this.checkValidateButton.emit(this.field.value !== 'OTHERS');
                return;
            }
            this.clearAllAuthenticationFieldsIfTypeChanged();
            if (this.field.id === 'type') {
                this.checkValidateButton.emit(authorizationSystem === 'OTHERS' && this.field.value === 'OAUTH');
                return;
            }
        }
    };

    clearAllAuthenticationFieldsIfTypeChanged = () => {
        if (this.field.id !== 'type') return;
        const tabFields = this.parentRecord?.recordData?.subSections
            ?.find(section => section.structure === 'POSTMAN')
            ?.tabs?.find(tab => tab.id === 'authentication')?.fields;
        const typeField = tabFields?.find(field => field.id === 'type');
        if (typeField.value === 'NOAUTH') {
            tabFields?.forEach(field => {
                if (field.id !== 'type') {
                    field.value = undefined;
                }
            });
        }
    };

    createOptionClick = () => {
        switch (this.field.id) {
            case 'authorizationSystem':
                this._utils.navigateToAuthorizationConfig('create-new');
                break;
            case 'sourceSystem':
                if (this.inConnectorsUI) {
                    const dialogRef = this.dialog.open(EditAuthorizationSystemComponent, {
                        panelClass: ['w-[90%]', 'max-w-full', 'h-[90%]', '[&>.mat-dialog-container]:p-0'],
                        data: {},
                        disableClose: true,
                    });
                    dialogRef.componentInstance.modal = true;
                }
        }
    };

    editOptionClick = (option: any) => {
        switch (this.field.id) {
            case 'authorizationSystem':
                this._utils.navigateToAuthorizationConfig(option?.id);
                break;
            case 'sourceSystem':
                if (this.inConnectorsUI) {
                    const dialogRef = this.dialog.open(EditAuthorizationSystemComponent, {
                        panelClass: ['w-[90%]', 'max-w-full', 'h-[90%]', '[&>.mat-dialog-container]:p-0'],
                        data: {},
                        disableClose: true,
                    });
                    dialogRef.componentInstance.modal = true;
                    dialogRef.componentInstance.configId = option.value;
                } else {
                    this._utils.navigateToAuthorizationConfig(option?.id);
                }
                break;
        }
    };

    dynamicParamsChanged = (value: string) => {
        const options = this.field.options;
        const option = options.find(option => option.id === value);
        if (!option) {
            return;
        }
        const dataTypeField = (this.record as any).fields.find(field => field.id === 'fieldType');
        dataTypeField.value = (option as any).fieldType;
        const additionalFieldRequiredField = (this.record as any).fields.find(field => field.id === 'additionalFieldRequired');
        additionalFieldRequiredField.value = (option as any).additionalFieldRequired;
    };

    public removeFile = (index: number) => {
        const fileName = this.fileNames[index];
        this.fileNames.splice(index, 1);
        if (this.field.value?.[fileName]) {
            delete this.field.value[fileName];
        }
    };

    public onFileSelected = (event: Event): void => {
        const input = event.target as HTMLInputElement;
        if (input?.files) {
            Array.from(input.files).forEach(file => {
                this.field.value = this.field.value || {};
                this.field.value[file.name] = file;
            });
            this.fileNames = Object.keys(this.field.value);
        }
    };

    isFormValid = () => {
        setTimeout(() => {
            this._integrationUtils.isFormValid(this.record, this.field, this.subSectionRecord);
        }, 100);
    };

    chainOptionSelected = option => {
        this.field.value = option?.['chainName'];
        this.chainChanged.emit(option);
    };

    optionValueChanged = (value: any) => {
        this.field.value = value;
        this.field.onChange && this.field.onChange(this.field.value, this.field);
        this.isFormValid();
        this.changeValidateButton();
        this.checkAndAddNewRow?.();
        if (this.field.callback) {
            this.store$.dispatch(
                IntegrationFieldValueChanged({
                    field: CommonUtilsService.cloneObject(this.field),
                })
            );
        }
    };

    ngOnInit() {
        (this.record as any)?.id && this.changeValidateButton();
        if (this.field.referenceFieldId) {
            this.referenceField = (this.parentRecord ? this.parentRecord : this.record).getRecordField(this.field.referenceFieldId);
        }
        if (this.field.value === undefined && this.field?.defaultValue !== undefined) {
            this.field.value = this.field?.defaultValue;
        }
        if (this.field?.type === 'FILE' && typeof this.field.value === 'object') {
            if (Array.isArray(this.field.value)) {
                this.fileNames = this.field.value.reduce((acc, file) => [...acc, file.name], []);
            } else {
                this.fileNames = Object.keys(this.field.value);
            }
        }
    }

    ngAfterViewInit() {}
}
